import {
  abbreviateNumber,
  americanDateFormat,
  centsToDollars,
  dollarsToCents,
  formatDollarsFromCents,
  Maybe,
  AnalyticIdentifier,
} from '@common/neo.tax';
import { Alert, Badge, Checkbox, CountryCell, Icon, Stack, Typography, useModal } from '@ui-components/neo.tax';
import { ReactComponent as PayrollIcon } from '@ui-components/neo.tax/dist/icons/single-neutral.svg';
import { ReactComponent as ChevronDownIcon } from '@ui-components/neo.tax/src/assets/icons/chevron-down.svg';
import * as Ariakit from 'ariakit';
import compact from 'lodash/compact';
import uniqueId from 'lodash/uniqueId';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import { RD_CATEGORY_SINGULAR_LABELS } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import {
  useGetRdExpenseProjectAllocationReport,
  useRdPercentageBulkApply,
  useUploadRdExpenseProjectAllocationReport,
} from '@neo.tax/rd/hooks';
import { PayrollQre, usePayrollQres } from '@neo.tax/rd/hooks/usePayrollQres';
import { PayrollQreSummary, usePayrollQreSummary } from '@neo.tax/rd/hooks/usePayrollQreSummary';
import { RdLockPageLayout } from '@neo.tax/rd/organisms/rd-lock-page-layout';
import {
  advancedFilter,
  FilterSortDataType,
  getCountryCodeAlpha2,
  getPath,
  Path,
  PayrollQreSortFields,
  PAYROLL_PROVIDER_NAMES,
  ProviderType,
  SortDirection,
  useBackgroundOperations,
  useColumnVisibility,
  useCurrentCompanyId,
  useCurrentUserScopeForCurrentCompany,
  useFilterConditions,
  useKeywordSearch,
  useSorting,
  type ColumnDefinition,
  type ProviderUnion,
  type SyncedProviders,
  keywordFilter,
  PercentCell,
  TABLE_CELL_PLACEHOLDER,
  SourcesContent,
  TableHeader,
  type PayrollQreFilter,
} from '@neo.tax/shared';
import {
  BulkAllocationModal,
  ExpenseLayout,
  FooterCell,
  MenuItem,
  NavLinkTo,
  TypographyFullWidth,
} from '@neo.tax/shared/organisms';
import { ApplyRDPercentageToExpensesButton } from '@neo.tax/shared/organisms/expense-layout/expense-layout.styled';

import { BulkApplySupervisorRdPercentageModal } from '../bulk-apply-supervisor-rd-percentage-modal';
import { PayrollQreDetail } from '../payroll-qre-detail';
import { EditQreExpenseAllocationModalLoader } from '../qre-allocation';
import { StyledSetRdPercentageMenu } from './payroll-qre-layout.styled';
import { PayrollWageNameCell } from './PayrollWageNameCell';
import { usePayrollQreLayoutAnalyticIds } from './usePayrollQreLayoutAnalyticIds';

type PayrollQreCell = CellProps<PayrollQre, Maybe<string>> & UseRowSelectInstanceProps<PayrollQre>;

const payrollQreTableColumns =
  (
    footerData: PayrollQreSummary | null,
    isPiiRedacted: boolean,
    limitAccessToFinancialData: boolean,
    modalOptions: {
      toggle: () => void;
      setSelectedRdExpenseId: (rdExpenseId: string) => void;
    },
    visibleColumns: PayrollQreSortFields[],
  ) =>
  ({
    handleClickRow,
    handleSelectAllRows,
    handleSelectRow,
    rowCount,
    selectedRowIds,
    syncedProviders,
  }: {
    handleClickRow: (cell: PayrollQreCell) => () => void;
    handleSelectAllRows: (checked: boolean) => void;
    handleSelectRow: (cell: PayrollQreCell) => (checked: boolean) => void;
    rowCount: number;
    selectedRowIds: string[];
    syncedProviders?: SyncedProviders;
  }): Column<PayrollQre>[] => {
    const amountCentsColumns: Column<PayrollQre>[] = limitAccessToFinancialData
      ? []
      : [
          {
            id: PayrollQreSortFields.QualifiedAmountCents,
            accessor: 'qualifiedAmountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Qualified $</TableHeader>,
            Cell: ({ value }: CellProps<PayrollQre, PayrollQre['qualifiedAmountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.qualifiedAmountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.qualifiedAmountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
          {
            id: PayrollQreSortFields.AmountCents,
            accessor: 'amountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Amount $</TableHeader>,
            Cell: ({ value }: CellProps<PayrollQre, PayrollQre['amountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.amountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.amountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
        ];

    const columns: Column<PayrollQre>[] = [
      {
        id: PayrollQreSortFields.Name,
        accessor: 'name',
        width: 'minmax(300px, 400px)',
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="expenseIds"
              name="expenseIds"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={handleSelectAllRows}
            />
            <TableHeader>Name</TableHeader>
          </Stack>
        ),
        Cell: (
          cell: CellProps<PayrollQre, PayrollQre['name']> & UseRowSelectInstanceProps<PayrollQre>,
        ): ReactElement => (
          <Stack direction="row">
            <Checkbox
              id={cell.row.original.id}
              name={cell.row.original.id}
              checked={selectedRowIds?.includes(cell.row.original.id)}
              onChange={handleSelectRow(cell)}
            />

            <PayrollWageNameCell
              name={cell.value}
              hasPayrollExpense={!!cell.row.original.payrollExpenseId}
              isPiiRedacted={isPiiRedacted}
              onClick={handleClickRow(cell)}
            />
          </Stack>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.count?.name ?? '0';
          return <FooterCell preText="COUNT" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.Email,
        accessor: 'email',
        width: 'minmax(220px, 320px)',
        Header: () => <TableHeader>Email</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['email']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
      {
        id: PayrollQreSortFields.Title,
        accessor: 'title',
        width: 190,
        Header: () => <TableHeader>Title</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['title']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.title ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.Department,
        accessor: 'department',
        width: 190,
        Header: () => <TableHeader>Department</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['department']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.department ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.ManagerName,
        accessor: 'managerName',
        width: 190,
        Header: () => <TableHeader>Manager</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['managerName']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.managerName ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.HireDate,
        accessor: 'hireDate',
        width: 120,
        Header: () => <TableHeader>Hire Date</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['hireDate']>): ReactElement => (
          <Typography color="inherit" fontSize="inherit">
            {value ? americanDateFormat(value, { zeroPadded: false }) : TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
      {
        id: PayrollQreSortFields.TerminationDate,
        accessor: 'terminationDate',
        width: 120,
        Header: () => <TableHeader>Term. Date</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['terminationDate']>): ReactElement => (
          <Typography color="inherit" fontSize="inherit">
            {value ? americanDateFormat(value, { zeroPadded: false }) : TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
      {
        id: PayrollQreSortFields.CostCenter,
        accessor: 'costCenter',
        width: 190,
        Header: () => <TableHeader>Cost Center</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['costCenter']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.costCenter ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.Entity,
        accessor: 'entity',
        width: 190,
        Header: () => <TableHeader>Entity</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['entity']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.entity ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      ...amountCentsColumns,
      {
        id: PayrollQreSortFields.QreRdPercentage,
        accessor: 'qreRdPercentage',
        width: 105,
        Header: () => <TableHeader textAlign="right">R&D %</TableHeader>,
        Cell: ({ row, value }: CellProps<PayrollQre, PayrollQre['qreRdPercentage']>): ReactElement => (
          <PercentCell
            onClick={(): void => {
              modalOptions.setSelectedRdExpenseId(row.original.id);
              modalOptions.toggle();
            }}
            percent={value}
            isSelected={row.isSelected}
          />
        ),
        Footer: (): ReactElement => {
          const hasData = (footerData?.avg?.qreRdPercentage ?? null) !== null;
          const value = hasData ? `${Math.round(footerData?.avg?.qreRdPercentage ?? 0)}%` : '0';
          return <FooterCell preText="AVG" text={value} alignText="end" />;
        },
      },
      {
        id: PayrollQreSortFields.PayrollProvider,
        accessor: (datum: PayrollQre): ProviderUnion[] => compact([datum.payrollProvider, ...datum.ticketingProviders]),
        Cell: ({ value }: CellProps<PayrollQre, ProviderUnion[]>): ReactElement => (
          <SourcesContent linkedProviders={value} allProviders={syncedProviders} placeholder={TABLE_CELL_PLACEHOLDER} />
        ),
        Header: () => <TableHeader>Sources</TableHeader>,
      },
      {
        id: PayrollQreSortFields.Country,
        accessor: (datum: PayrollQre): string | null | undefined => datum.country,
        width: 110,
        Header: () => <TableHeader>Country</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, string | null | undefined>): ReactElement | string =>
          typeof value === 'string' && value.length ? (
            <CountryCell countryCode={getCountryCodeAlpha2(value)}>{value}</CountryCell>
          ) : (
            TABLE_CELL_PLACEHOLDER
          ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.country ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.State,
        accessor: (datum: PayrollQre): string | null | undefined => datum.state,
        width: 110,
        Header: () => <TableHeader>US State</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, string | null | undefined>): string => value || TABLE_CELL_PLACEHOLDER,
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.state ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: PayrollQreSortFields.RdCategory,
        accessor: 'rdCategory',
        width: 170,
        Header: () => <TableHeader>R&D Category</TableHeader>,
        Cell: ({ value }: CellProps<PayrollQre, PayrollQre['rdCategory']>): ReactElement => (
          <Badge colorVariant="green" icon={<PayrollIcon />}>
            {RD_CATEGORY_SINGULAR_LABELS[value]}
          </Badge>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.rdCategory ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
    ];

    return columns.filter((column) => visibleColumns.includes(column.id as PayrollQreSortFields));
  };

const sortAndFilterableColumnsForNonFinancialUser = [
  { name: PayrollQreSortFields.Name, label: 'Name', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.Email, label: 'Email', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.Title, label: 'Title', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.Department, label: 'Department', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.ManagerName, label: 'Manager', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.HireDate, label: 'Hire Date', dataType: FilterSortDataType.DATE },
  { name: PayrollQreSortFields.TerminationDate, label: 'Termination Date', dataType: FilterSortDataType.DATE },
  { name: PayrollQreSortFields.CostCenter, label: 'Cost Center', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.Entity, label: 'Entity', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.QreRdPercentage, label: 'R&D %', dataType: FilterSortDataType.NUMBER },
  {
    name: PayrollQreSortFields.PayrollProvider,
    label: 'Source',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(PAYROLL_PROVIDER_NAMES).map(([value, label]) => ({ value, label })),
  },
  { name: PayrollQreSortFields.Country, label: 'Country', dataType: FilterSortDataType.TEXT },
  { name: PayrollQreSortFields.State, label: 'US State', dataType: FilterSortDataType.TEXT },
  {
    name: PayrollQreSortFields.RdCategory,
    label: 'R&D Category',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(RD_CATEGORY_SINGULAR_LABELS).map(([value, label]) => ({ value, label })),
  },
  { name: PayrollQreSortFields.IsMatched, label: 'Matched', dataType: FilterSortDataType.BOOLEAN },
] as const satisfies readonly ColumnDefinition<PayrollQreSortFields>[];

const indexOfEntityColumn = sortAndFilterableColumnsForNonFinancialUser.findIndex(
  (column) => column.name === PayrollQreSortFields.Entity,
);
const sortAndFilterableColumnsForFullFinancialUser = [
  ...sortAndFilterableColumnsForNonFinancialUser.slice(0, indexOfEntityColumn + 1),
  {
    name: PayrollQreSortFields.QualifiedAmountCents,
    label: 'Qualified $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: PayrollQreSortFields.AmountCents,
    label: 'Amount $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  ...sortAndFilterableColumnsForNonFinancialUser.slice(indexOfEntityColumn + 1),
] as const satisfies readonly ColumnDefinition<PayrollQreSortFields>[];
const sortAndFilterableColumnsForPiiRedactedUser = [...sortAndFilterableColumnsForFullFinancialUser.slice(1)];

export const PayrollQreLayout = (): ReactElement => {
  const { isOpen, setIsOpen, toggle } = useModal();
  const [selectedRdExpenseId, setSelectedRdExpenseId] = useState<string | undefined>(undefined);
  const { isPiiRedacted, limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const searchKeywordState = useKeywordSearch();

  // Poll for background operations to evict company cache when R&D cost file import completes
  useBackgroundOperations();

  const sortAndFilterableColumns = limitAccessToFinancialData
    ? sortAndFilterableColumnsForNonFinancialUser
    : isPiiRedacted
      ? sortAndFilterableColumnsForPiiRedactedUser
      : sortAndFilterableColumnsForFullFinancialUser;
  const initialSorting = [
    {
      id: uniqueId('PayrollQreSortField'),
      column: PayrollQreSortFields.QreRdPercentage,
      direction: SortDirection.Desc,
    },
  ];
  if (!limitAccessToFinancialData) {
    initialSorting.push({
      id: uniqueId('PayrollQreSortField'),
      column: PayrollQreSortFields.AmountCents,
      direction: SortDirection.Desc,
    });
  }

  const filterState = useFilterConditions({ columns: sortAndFilterableColumns, persistenceKey: 'rdPayrollQre' });
  const sortState = useSorting({
    columns: sortAndFilterableColumns,
    initialSorting,
    persistenceKey: 'rdPayrollQre',
  });
  const columnVisibilityState = useColumnVisibility({
    columns: sortAndFilterableColumns,
    alwaysVisibleColumns: [PayrollQreSortFields.Name, PayrollQreSortFields.QreRdPercentage],
    initialVisibleColumns: [
      PayrollQreSortFields.Title,
      PayrollQreSortFields.Department,
      PayrollQreSortFields.ManagerName,
      PayrollQreSortFields.PayrollProvider,
      PayrollQreSortFields.Country,
      PayrollQreSortFields.State,
      PayrollQreSortFields.RdCategory,
      ...(limitAccessToFinancialData
        ? []
        : [PayrollQreSortFields.QualifiedAmountCents, PayrollQreSortFields.AmountCents]),
    ],
    persistenceKey: 'rdPayrollQre',
  });

  // memo inputs
  const sorting = useMemo(
    () => sortState.sorts.map((s) => ({ field: s.column, direction: s.direction })),
    [sortState.sorts],
  );

  const { isLoadingMore, loading, onLoadMoreItems, payrollQres } = usePayrollQres({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting,
  });
  const { loading: loadingSummary, payrollQreSummary } = usePayrollQreSummary({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  const hasSomeContributors = payrollQres?.some((row) => row.rdTaxCreditContributorId && !row.payrollExpenseId);

  // R&D-specific logic
  const companyId = useCurrentCompanyId();
  const { fiscalYear, fiscalYearId, isNowPastPeriodEndDate } = useRdContext();
  const { download: downloadBulkEditTemplate, loading: isDownloadingBulkEditTemplate } =
    useGetRdExpenseProjectAllocationReport(fiscalYearId, ProviderType.Payroll);
  const bulkEditUpload = useUploadRdExpenseProjectAllocationReport(fiscalYearId, ProviderType.Payroll);

  const payrollQreFilter = useMemo(
    () =>
      ({
        and: [
          keywordFilter<PayrollQreFilter>(['name', 'email', 'title'], searchKeywordState.searchKeyword),
          advancedFilter<PayrollQreFilter>(filterState.conditions),
        ],
      }) as Record<string, unknown>,
    [searchKeywordState.searchKeyword, filterState.conditions],
  );

  const {
    disableApplyRDPercentageToExpensesButton,
    hasRdEligibleProjects,
    isAllSelected,
    isApplyRDPercentageModalOpen,
    rdProjects,
    selectedRowIds,
    setIsAllSelected,
    setIsApplyRDPercentageModalOpen,
    setSelectedRows,
    submit,
  } = useRdPercentageBulkApply({
    fiscalYearId,
    loading,
    filter: payrollQreFilter,
  });

  // Clear select-all state when filters or search change so the banner
  // doesn't persist alongside rows that no longer match.
  useEffect(() => {
    setIsAllSelected(false);
    setSelectedRows([]);
  }, [filterState.conditions, searchKeywordState.searchKeyword, setIsAllSelected, setSelectedRows]);

  const totalCount = payrollQreSummary ? Number(payrollQreSummary.count?.name ?? 0) : undefined;

  const { isOpen: isSupervisorModalOpen, setIsOpen: setIsSupervisorModalOpen } = useModal();
  const setRdPercentageMenuState = Ariakit.useMenuState({ gutter: 1 });

  // Map selected RD expense IDs to payroll expense IDs for supervisor analysis
  const selectedPayrollExpenseIds = useMemo(() => {
    if (!payrollQres) return [];
    return payrollQres
      .filter((qre) => selectedRowIds.includes(qre.id))
      .map((qre) => qre.payrollExpenseId)
      .filter((id): id is string => id != null);
  }, [payrollQres, selectedRowIds]);

  const rdAlert = rdProjects && !hasRdEligibleProjects && (
    <Alert
      severity="warning"
      title="There are no eligible R&D projects. Go to Projects page to edit project eligibility."
      actions={[
        {
          label: 'Go To Projects',
          as: NavLinkTo,
          to: getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId }),
        },
      ]}
    />
  );

  const analyticIds = usePayrollQreLayoutAnalyticIds();

  const handleManualMethodClick = (): void => {
    setIsApplyRDPercentageModalOpen(true);
  };

  const handleSupervisorFormulaClick = (): void => {
    setIsSupervisorModalOpen(true);
  };

  const toolbarActions = (
    <>
      <ApplyRDPercentageToExpensesButton
        as={Ariakit.MenuButton}
        state={setRdPercentageMenuState}
        size="base"
        variant="primary"
        rounded="regular"
        disabled={disableApplyRDPercentageToExpensesButton}
        id={analyticIds.setRdPercentageBtnId}
      >
        <Stack gap={2} alignItems="center">
          Set R&D %
          <Icon size={4} node={<ChevronDownIcon />} />
        </Stack>
      </ApplyRDPercentageToExpensesButton>
      <StyledSetRdPercentageMenu state={setRdPercentageMenuState}>
        <MenuItem onClick={handleManualMethodClick}>Manual</MenuItem>
        {!limitAccessToFinancialData && <MenuItem onClick={handleSupervisorFormulaClick}>Supervisor</MenuItem>}
      </StyledSetRdPercentageMenu>
    </>
  );

  return (
    <>
      <ExpenseLayout
        pageTitle="Wage QREs"
        pageSubtitle="Click on a team member to review and modify details."
        connectableProviderTypes={new Set([ProviderType.Payroll])}
        connectProviderLabel="Connect Payroll"
        bulkEditProviderType={ProviderType.Payroll}
        searchKeywordState={searchKeywordState}
        filterState={filterState}
        sortState={sortState}
        columnVisibilityState={columnVisibilityState}
        createColumns={payrollQreTableColumns(
          payrollQreSummary,
          isPiiRedacted,
          limitAccessToFinancialData,
          {
            toggle,
            setSelectedRdExpenseId,
          },
          columnVisibilityState.visibleColumns,
        )}
        tableRows={payrollQres}
        loadingRows={loading}
        loadingFooter={loadingSummary || isLoadingMore}
        isPiiRedacted={isPiiRedacted}
        onLoadMoreItems={onLoadMoreItems}
        ExpenseDetail={PayrollQreDetail}
        LockPageLayout={RdLockPageLayout}
        lockPageLayoutProps={{ disableIfRdCostPendingValidation: true, disableIfSupervisorAllocationInProgress: true }}
        fiscalYear={fiscalYear}
        fiscalYearId={fiscalYearId}
        isNowPastPeriodEndDate={isNowPastPeriodEndDate}
        toolbarActions={toolbarActions}
        onRowSelectionChange={setSelectedRows}
        totalCount={totalCount}
        isAllSelected={isAllSelected}
        onSelectAllChange={setIsAllSelected}
        analyticIds={analyticIds}
        bulkEditTemplate={{
          onDownload: downloadBulkEditTemplate,
          isDownloading: isDownloadingBulkEditTemplate,
        }}
        bulkEditUpload={bulkEditUpload}
        alert={
          <>
            {rdAlert}
            {hasSomeContributors && (
              <Alert
                severity="warning"
                title="Team members with a yellow alert icon have not been linked to payroll data. Please connect your payroll system."
              />
            )}
          </>
        }
      />
      {selectedRdExpenseId && (
        <EditQreExpenseAllocationModalLoader
          isOpen={isOpen}
          close={(): void => setIsOpen(false)}
          rdExpenseId={selectedRdExpenseId}
        />
      )}
      <BulkAllocationModal
        isOpen={isApplyRDPercentageModalOpen}
        loading={loading}
        setIsOpen={setIsApplyRDPercentageModalOpen}
        submit={submit}
        headerText={
          isAllSelected
            ? `Set R&D % for all ${totalCount ?? ''} Wage QREs`
            : `Set R&D % for ${selectedRowIds.length} ${selectedRowIds.length === 1 ? 'Wage QRE' : 'Wage QREs'}`
        }
        sliderLabelText="R&D %"
        copy={
          <Stack gap={6} paddingY={6} direction="column">
            <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
              Enter the target R&D percentage. This will replace existing project allocations for the selected wage
              QREs.
            </Typography>
            <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
              The R&D amount will be distributed proportionally across qualified R&D projects based on their current
              share of qualified employee expenses.
            </Typography>
            {isAllSelected && (
              <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
                All wage QREs are selected, including those not currently visible on the page.
              </Typography>
            )}
          </Stack>
        }
        cancelButtonId={AnalyticIdentifier.QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_CANCEL_BTN}
        submitButtonId={AnalyticIdentifier.QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_TO_EXPENSES_MODAL_SUBMIT_BTN}
      />
      <BulkApplySupervisorRdPercentageModal
        isOpen={isSupervisorModalOpen}
        setIsOpen={setIsSupervisorModalOpen}
        fiscalYearId={fiscalYearId}
        selectedExpenseIds={selectedPayrollExpenseIds}
        isAllSelected={isAllSelected}
        filter={payrollQreFilter}
        onApplySuccess={(): void => setIsSupervisorModalOpen(false)}
      />
    </>
  );
};
